import { CaretRight } from 'phosphor-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import { TOAST_SUCCESS_DEFAULT_OPTIONS } from '../../../../constants/toast'
import { api } from '../../../../services/api'
import { useExpertRemoveMutation } from '../../../../services/hooks/experts/mutations/useExpertRemoveMutation'
import { type ExpertDomain } from '../../../../types/domain/expert-domain'
import { TableRowActions } from '../../Registers/components/TableRowActions'
import { ButtonPage } from './components/ButtonPage'
import { BankData } from './pages/Data/BankData'
import { HistoricData } from './pages/Data/HistoricData'
import { InternationalBankData } from './pages/Data/InternationalBankData'
import { InternationalData } from './pages/Data/InternationalData'
import { LegalPersonData } from './pages/Data/LegalPersonData'
import { NaturalPersonData } from './pages/Data/NaturalPersonData'
import { ProfessionalData } from './pages/Data/ProfessionalData'
import { SkillsData } from './pages/Data/SkillsData'
import { BankForm } from './pages/Forms/BankForm'
import { InternationalBankForm } from './pages/Forms/InternationalBankForm'
import { InternationalPersonForm } from './pages/Forms/InternationalPersonForm'
import { LegalPersonForm } from './pages/Forms/LegalPersonForm'
import { NaturalPersonForm } from './pages/Forms/NaturalPersonForm'
import { ProfessionalForm } from './pages/Forms/ProfessionalForm'

enum Page {
  PERSONAL = 'personal_data',
  SKILLS = 'skills',
  PROFESSIONAL = 'professional_data',
  BANK = 'bank_data',
  HISTORIC = 'historic_data',

  PERSONAL_FORM = 'personal_form',
  PROFESSIONAL_FORM = 'professional_form',
  BANK_FORM = 'bank_form',
}

export const DatabaseDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [page, setPage] = useState<string>('personal_data')
  const [dataExpert, setDataExpert] = useState<ExpertDomain>()
  const expertRemotionMutation = useExpertRemoveMutation()
  // eslint-disable-next-line
  const [formModalIsOpen, setFormModalIsOpen] = useState(false)

  useEffect(() => {
    fetchExpertData()
  }, [])

  const handleRemoveExpert = useCallback(() => {
    const id = dataExpert!.id
    expertRemotionMutation.mutate(id, {
      onSuccess: () => {
        toast.success(
          'Expert deletado com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )

        setFormModalIsOpen(false)
        navigate('/painel/database')
      },
    })
  }, [expertRemotionMutation])

  const fetchExpertData = () => {
    api
      .get(`/experts/${id}`)
      .then((result) => {
        setDataExpert(result.data)
      })
      .catch(() => {
        setDataExpert(undefined)
      })
  }

  const editPersonalData = () => {
    setPage(Page.PERSONAL_FORM)
  }

  const editBankData = () => {
    setPage(Page.BANK_FORM)
  }

  const editProfessionalData = () => {
    setPage(Page.PROFESSIONAL_FORM)
  }

  const savePersonalData = () => {
    setPage(Page.PERSONAL)
    fetchExpertData()
  }

  const saveBankData = () => {
    setPage(Page.BANK)
    fetchExpertData()
  }

  const saveProfessionalData = () => {
    setPage(Page.PROFESSIONAL)
    fetchExpertData()
  }

  return (
    <div className="w-full pb-20 lg:px-52 pt-11">
      <div className="flex items-center gap-3 mx-6 mb-10 text-xs text-primary-light lg:text-base">
        <a
          className="underline cursor-pointer underline-offset-2"
          onClick={() => navigate('/painel/database')}
        >
          Base de dados
        </a>
        <CaretRight size={16} />
        <a
          className="underline cursor-pointer underline-offset-2"
          onClick={() => navigate(`/painel/database/${id}`)}
        >
          mais informações
        </a>
      </div>

      {dataExpert ? (
        <div>
          <div className="flex justify-between mx-6 mb-12">
            <div>
              <p className="text-base">{dataExpert?.name}</p>
              <p className="text-sm text-grey-700">{dataExpert?.email}</p>
            </div>
            <div>
              <TableRowActions
                tableContentType="Expert"
                isRemovingRegister={expertRemotionMutation.isLoading}
                alertMessage="Você está prestes a excluir um expert. Deseja continuar?"
                onRemove={handleRemoveExpert}
              />
            </div>
          </div>

          <div className="flex w-full gap-3 px-6 mb-12 overflow-scroll text-xs lg:gap-10 scrolltransparent lg:text-base">
            <ButtonPage
              activated={page === Page.PERSONAL || page === Page.PERSONAL_FORM}
              onClick={() => setPage(Page.PERSONAL)}
            >
              Informações pessoais
            </ButtonPage>

            <ButtonPage
              activated={
                page === Page.PROFESSIONAL || page === Page.PROFESSIONAL_FORM
              }
              onClick={() => setPage(Page.PROFESSIONAL)}
            >
              Dados profissionais
            </ButtonPage>

            <ButtonPage
              activated={page === Page.SKILLS}
              onClick={() => setPage(Page.SKILLS)}
            >
              Habilidades
            </ButtonPage>

            <ButtonPage
              activated={page === Page.BANK || page === Page.BANK_FORM}
              onClick={() => setPage(Page.BANK)}
            >
              Dados bancários
            </ButtonPage>

            <ButtonPage
              activated={page === Page.HISTORIC}
              onClick={() => setPage(Page.HISTORIC)}
            >
              Histórico
            </ButtonPage>
          </div>

          <div className="mx-6">
            {/* Data ------------------------------------------------ */}
            {page === Page.PERSONAL && (
              <>
                {dataExpert?.personType === 'natural_person' && (
                  <NaturalPersonData
                    data={dataExpert}
                    edit={editPersonalData}
                  />
                )}
                {dataExpert?.personType === 'legal_person' && (
                  <LegalPersonData data={dataExpert} edit={editPersonalData} />
                )}
                {dataExpert?.personType === 'international_person' && (
                  <InternationalData
                    data={dataExpert}
                    edit={editPersonalData}
                  />
                )}
              </>
            )}

            {page === Page.PROFESSIONAL && (
              <ProfessionalData data={dataExpert} edit={editProfessionalData} />
            )}

            {page === Page.SKILLS && <SkillsData id={id} />}

            {page === Page.BANK && (
              <>
                {dataExpert?.personType === 'international_person' ? (
                  <InternationalBankData
                    data={dataExpert}
                    edit={editBankData}
                  />
                ) : (
                  <BankData data={dataExpert} edit={editBankData} />
                )}
              </>
            )}

            {page === Page.HISTORIC && dataExpert && (
              <HistoricData expert={dataExpert} />
            )}

            {/* Form ------------------------------------------------ */}
            {page === Page.PERSONAL_FORM && (
              <>
                {dataExpert?.personType === 'natural_person' && (
                  <NaturalPersonForm
                    data={dataExpert}
                    save={savePersonalData}
                  />
                )}
                {dataExpert?.personType === 'legal_person' && (
                  <LegalPersonForm data={dataExpert} save={savePersonalData} />
                )}
                {dataExpert?.personType === 'international_person' && (
                  <InternationalPersonForm
                    data={dataExpert}
                    save={savePersonalData}
                  />
                )}
              </>
            )}
            {page === Page.PROFESSIONAL_FORM && (
              <ProfessionalForm data={dataExpert} save={saveProfessionalData} />
            )}
            {page === Page.BANK_FORM && (
              <>
                {dataExpert?.personType === 'international_person' ? (
                  <InternationalBankForm
                    data={dataExpert}
                    save={saveBankData}
                  />
                ) : (
                  <BankForm data={dataExpert} save={saveBankData} />
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="px-6">Não foi possível carregar os dados</div>
      )}
    </div>
  )
}
